import { useEffect, useRef, useState } from 'react';

/**
 * Non-render logic for AuditedStatementSection's sticky statement header (RECUR-27656):
 * GitHub-PR-style `stuck` detection via a sentinel IntersectionObserver, the measured
 * header-clone widths, and horizontal-scroll sync between the real table and the pinned clone.
 */
export function useAuditedStatementSectionLogic() {
    const [stuck, setStuck] = useState(false);
    const [headerWidths, setHeaderWidths] = useState<number[]>([]);
    const sentinelRef = useRef<HTMLDivElement | null>(null);
    const cloneViewportRef = useRef<HTMLDivElement | null>(null);
    const lastScrollLeftRef = useRef(0);

    const handleHorizontalScroll = (x: number) => {
        lastScrollLeftRef.current = x;
        if (cloneViewportRef.current) cloneViewportRef.current.scrollLeft = x;
    };

    // RECUR-27656 — GitHub-PR-style sticky statement header. `stuck` flips once the 1px sentinel
    // above the section scrolls past the top of the scroller (`.MainContentWrapper`); the pinned
    // band then shows a scroll-synced CLONE of the table's year-column header row (the real
    // `<thead>` can't pin — see AuditedStatementTable's `.TableContainer` comment).
    useEffect(() => {
        const el = sentinelRef.current;
        if (!el || typeof IntersectionObserver === 'undefined') return undefined;
        const observer = new IntersectionObserver(([entry]) => setStuck(!entry.isIntersecting), { root: null });
        observer.observe(el);
        return () => observer.disconnect();
    }, []);

    // Re-apply the last known scroll position whenever the clone (re)mounts (it only renders
    // while stuck + expanded), so it never opens scrolled to the wrong FY.
    useEffect(() => {
        if (cloneViewportRef.current) cloneViewportRef.current.scrollLeft = lastScrollLeftRef.current;
    }, [stuck, headerWidths]);

    return { stuck, headerWidths, setHeaderWidths, sentinelRef, cloneViewportRef, handleHorizontalScroll };
}
